import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { DrawerSetting, GridColumn } from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import { DataType } from '@shared/helpers/enums/data-type';
import { CurrentUserService } from '@shared/services/current-user.service';
import { QueryOptions } from 'odata-query';
import { isGstarSystemUser } from '../../helpers/is-gstar-system-user';
import {
  isSignedInUsersRow,
  signedInSnapshot,
} from '../../helpers/is-signed-in-users-row';
import {
  Collection,
  ExternalUser,
  GridItemCreateEvent,
  GridItemUpdateEvent,
} from '../../models';
import { UpsertExternalUserComponent } from '../upsert-external-user/upsert-external-user.component';
import { UserEngagementsCellComponent } from '../user-engagements-cell/user-engagements-cell.component';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-external-users-grid',
  templateUrl: 'external-users-grid.component.html',
})
export class ExternalUsersGridComponent {
  private translateService = inject(TranslateService);
  private readonly currentUserService = inject(CurrentUserService);

  @Input({ required: true }) source: Collection<ExternalUser> = {
    total: 0,
    items: [],
  };

  @Input() query!: Partial<QueryOptions<ExternalUser>>;
  @Output() queryChange = new EventEmitter<
    Partial<QueryOptions<ExternalUser>>
  >();

  @Output() create = new EventEmitter<GridItemCreateEvent<ExternalUser>>();

  @Output() update = new EventEmitter<GridItemUpdateEvent<ExternalUser>>();

  @Output() deleteRequested = new EventEmitter<ExternalUser>();

  drawerSetting: DrawerSetting<ExternalUser, ExternalUser> = {
    create: {
      title: this.translateService.instant(
        'admin.grid.createExternalUserTitle'
      ),
      component: UpsertExternalUserComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.create'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.create.emit({ item: item.model!, onSuccess: closeFn });
            }
          },
          isPrimary: true,
        },
      ],
    },
    edit: {
      title: this.translateService.instant(
        'admin.grid.updateExternalUserTitle'
      ),
      component: UpsertExternalUserComponent,
      buttons: [
        {
          label: this.translateService.instant('admin.grid.cancel'),
          command: (closeFn): void => {
            closeFn();
          },
          isPrimary: false,
        },
        {
          label: this.translateService.instant('admin.grid.update'),
          command: (closeFn, item): void => {
            if (item?.isValid) {
              this.update.emit({
                item: item.model!,
                prevItem: item.prevModel,
                onSuccess: closeFn,
              });
            }
          },
          isPrimary: true,
        },
      ],
    },
  };

  headers: GridColumn[] = [
    {
      label: this.translateService.instant('admin.user.userColumn'),
      key: 'fullName',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('externalUser.email'),
      key: 'email',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('admin.user.engagementsColumn'),
      key: 'engagementAssignments',
      dataType: DataType.Template,
      templateCell: UserEngagementsCellComponent,
    },
    {
      label: this.translateService.instant('externalUser.organization'),
      key: 'organization',
      dataType: DataType.String,
      isSortable: true,
    },
    {
      label: this.translateService.instant('externalUser.modified'),
      key: 'modified',
      dataType: DataType.Date,
      isSortable: true,
    },
    {
      label: this.translateService.instant('externalUser.isActive'),
      key: 'isActive',
      dataType: DataType.Boolean,
      isSortable: true,
    },
  ];

  searchSetting: SearchSetting<ExternalUser> = {
    filtersLogicalOperator: 'or',
    filters: [
      {
        dataKey: 'firstName',
        operator: 'startswith',
      },
      {
        dataKey: 'lastName',
        operator: 'startswith',
      },
      {
        dataKey: 'organization',
        operator: 'contains',
      },
      {
        dataKey: 'email',
        operator: 'contains',
      },
    ],
  };
  actionButtons: ActionButton<ExternalUser>[] = [
    {
      label: this.translateService.instant('admin.user.deleteUser'),
      icon: 'icon-delete-outline',
      disabledIf: (item: ExternalUser): boolean =>
        isGstarSystemUser(item) ||
        isSignedInUsersRow(
          { id: item.id, email: item.email },
          signedInSnapshot(this.currentUserService.currentUserInfo())
        ),
      command: (item: ExternalUser): void => {
        this.deleteRequested.emit(item);
      },
    },
  ];

  queryHandler(externalUserQuery: Partial<QueryOptions<ExternalUser>>): void {
    this.queryChange.emit(externalUserQuery);
  }
}
